import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, Star, Eye, Film, ExternalLink, Calendar, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

function getInitials(name) {
  return (name || "F").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return "";
  const A = 0x1F1E6;
  return String.fromCodePoint(A + iso2.toUpperCase().charCodeAt(0) - 65) + String.fromCodePoint(A + iso2.toUpperCase().charCodeAt(1) - 65);
}

export default function FilmmakerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [films, setFilms] = useState([]);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const loadProfile = () => {
    api.get(`/festival/filmmakers/${id}`).then((r) => {
      setProfile(r.data);
      setFollowing(r.data.following || false);
    }).catch((e) => setError(e?.response?.data?.detail || "Filmmaker not found"));
  };
  useEffect(() => {
    loadProfile();
    api.get(`/festival/submissions?filmmaker_user_id=${id}&sort=score`).then((r) => setFilms(r.data || [])).catch(() => setFilms([]));
  }, [id]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow filmmakers"); return; }
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/festival/filmmakers/${id}/follow`);
      setFollowing(data.following);
      setProfile((p) => p ? { ...p, follower_count: data.follower_count, following: data.following } : p);
      toast.success(data.following ? `Following ${profile?.name} — you'll get a push when they drop a new film` : `Unfollowed ${profile?.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle follow");
    } finally { setFollowBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-32 px-6 max-w-md mx-auto text-center">
        <Film className="w-12 h-12 text-[#E50914] mx-auto mb-4" />
        <p className="text-gray-400 mb-4">{error}</p>
        <Link to="/festival" className="btn-red px-6 py-2 rounded font-semibold">Back to festival</Link>
      </div>
    </div>
  );
  if (!profile) return <div className="min-h-screen bg-[#0A0A0A]"><Navbar /></div>;

  const joined = profile.joined_at ? new Date(profile.joined_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-12 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/15 via-transparent to-[#E50914]/10 pointer-events-none" />
        <div className="relative max-w-[1300px] mx-auto flex items-start gap-8 flex-wrap">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {profile.picture ? (
              <img data-testid="profile-avatar" src={profile.picture} alt="" className="w-32 h-32 rounded-full object-cover border-2 border-amber-400/30" />
            ) : (
              <div data-testid="profile-avatar" className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-700/40 to-[#E50914]/40 flex items-center justify-center text-4xl font-black border-2 border-amber-400/30">
                {getInitials(profile.name)}
              </div>
            )}
          </div>
          {/* Identity */}
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">
              <Film className="w-3.5 h-3.5" /> Filmmaker
            </div>
            <h1 data-testid="profile-name" className="text-4xl sm:text-5xl font-black tracking-tight mb-2">{profile.name}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-400 flex-wrap">
              {profile.country && (
                <span className="flex items-center gap-1.5">
                  <span className="text-lg">{flagEmoji(profile.country)}</span>
                  <span className="font-semibold">{profile.country}</span>
                </span>
              )}
              {joined && (
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Joined {joined}</span>
              )}
              {profile.website && (
                <a data-testid="profile-website" href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-amber-400 hover:text-amber-300">
                  <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {profile.bio && (
              <p className="mt-4 text-gray-300 text-base leading-relaxed max-w-2xl">{profile.bio}</p>
            )}
            <div className="mt-5 flex items-center gap-4 flex-wrap">
              {user && user.user_id !== profile.user_id && (
                <button
                  data-testid="follow-btn"
                  onClick={toggleFollow}
                  disabled={followBusy}
                  className={`px-5 py-2 rounded font-semibold inline-flex items-center gap-2 transition-all border disabled:opacity-50 ${
                    following
                      ? "bg-amber-400/15 text-amber-300 border-amber-400/40 hover:bg-amber-400/25"
                      : "btn-red border-transparent"
                  }`}
                >
                  {followBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className={`w-4 h-4 ${following ? "fill-amber-300" : ""}`} />}
                  {following ? "Following" : "Follow"}
                </button>
              )}
              <span data-testid="follower-count" className="text-sm text-gray-400">
                <strong className="text-white">{profile.follower_count}</strong> follower{profile.follower_count === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 lg:px-12 pb-10">
        <div className="max-w-[1300px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          <div data-testid="stat-films" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Films</div>
            <div className="text-3xl font-black">{profile.total_films}</div>
          </div>
          <div data-testid="stat-views" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Views</div>
            <div className="text-3xl font-black flex items-center gap-1.5">{profile.total_views}<Eye className="w-5 h-5 text-gray-500" /></div>
          </div>
          <div data-testid="stat-votes" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Total votes</div>
            <div className="text-3xl font-black">{profile.total_votes}</div>
          </div>
          <div data-testid="stat-avg" className="bg-[#141414] border border-white/5 rounded-lg p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Avg rating</div>
            <div className="text-3xl font-black flex items-center gap-2 text-amber-400">{profile.avg_rating.toFixed(2)}<Star className="w-5 h-5 fill-amber-400" /></div>
          </div>
        </div>
      </section>

      {/* Films */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-[1300px] mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <Film className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-bold uppercase tracking-widest">Films at the Festival</h2>
          </div>
          {films.length === 0 ? (
            <div className="text-center py-16 text-gray-500 bg-[#0f0f0f] border border-dashed border-white/10 rounded-lg">
              <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No approved films yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {films.map((f) => (
                <Link
                  key={f.submission_id}
                  data-testid={`film-${f.submission_id}`}
                  to={`/festival/films/${f.submission_id}`}
                  className="group relative aspect-[2/3] rounded overflow-hidden bg-[#141414] hover:scale-[1.04] transition-transform"
                >
                  {f.poster_url ? (
                    <img src={f.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-600">
                      <Film className="w-10 h-10" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{f.region || "—"}</div>
                    <h3 className="text-sm font-bold leading-tight line-clamp-2">{f.title}</h3>
                    <div className="flex items-center gap-1 text-[11px] text-gray-300 mt-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="font-bold">{(f.score || 0).toFixed(2)}</span>
                      <span className="text-gray-500">· {f.total_votes || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
